/* Landing-page buy widget. Deliberately thin: adding to the cart, opening the drawer,
   the loading state and the error handling all belong to the theme's own product-form,
   which this component only feeds with the right variant id.

   Task 4b: switching formula now repaints the whole block, not just the form/price
   pair -- gallery, size-card prices, social proof, heading and "who it's for" all live
   OUTSIDE <lp-buy> itself, in the media/info columns sections/lp-buy.liquid renders
   around it. They are reached through [data-lp-root] (stamped once on the section's own
   grid wrapper), a data attribute rather than a class -- matching how this widget
   already only ever couples to markup through data-lp-* attributes, never classes (see
   snippets/lp-buy-form.liquid's own header). Colors (--lp-brand/--lp-brand-dark and,
   since review, --lp-brand-ink/--lp-lav/--lp-lav-100/--lp-lav-200) come in as
   data-lp-accent-* attributes computed by Liquid; this file never writes a color or any
   Hebrew string itself. */
class LpBuy extends HTMLElement {
  connectedCallback() {
    // Cached alongside the other refs this class keeps, mirroring how the theme's own
    // components cache DOM lookups in connectedCallback; not read elsewhere in this file
    // today, but this is where a future feature (e.g. focus handling) would reach for them.
    this.formulaInputs = this.querySelectorAll('[data-lp-formula]');
    this.sizeInputs = this.querySelectorAll('[data-lp-size]');
    // Falls back to itself if the section markup is ever missing [data-lp-root] (e.g. a
    // future caller that only wants the form/price mechanics) -- sync() would then simply
    // find no gallery/social-proof/heading/audience nodes to toggle, which is harmless.
    this.root = this.closest('[data-lp-root]') || this;
    // A11y pass (15.08.2026, task 10): announced from onChange() below, never from sync()
    // -- sync() also runs on connect and after cart:added, and this region must stay quiet
    // on page load and on a successful add-to-cart, not just on an actual formula switch.
    this.liveRegion = this.querySelector('[data-lp-live]');
    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);

    // Gallery thumbnails ([data-lp-thumb], sections/lp-buy.liquid) live outside <lp-buy>
    // itself, same as the gallery/social-proof/heading/audience nodes above -- reached
    // through this.root for the same reason. One listener on the root, not one per thumb:
    // both galleries can hold up to ten thumbs combined (main plus four more, per
    // formula), and a real <button> already gives Enter/Space activation for free, so
    // this only needs to handle `click`, never a separate keydown handler.
    this.onThumbClick = this.onThumbClick.bind(this);
    this.root.addEventListener('click', this.onThumbClick);

    // Owner fix (gallery report, 12.08.2026): warms up a formula's still-lazy frames
    // the moment the shopper's cursor or keyboard focus reaches its thumbnail strip --
    // see onGalleryWarm below for why this exists and what it actually does.
    // `mouseover` (bubbles), not `mouseenter` (does not), so one listener here can
    // catch every thumb via delegation the same way onThumbClick already does;
    // `focusin` (bubbles) is the focus equivalent of `mouseover` for the same reason.
    this.onGalleryWarm = this.onGalleryWarm.bind(this);
    this.root.addEventListener('mouseover', this.onGalleryWarm);
    this.root.addEventListener('focusin', this.onGalleryWarm);

    // Both fieldsets render `disabled` in the markup (snippets/lp-buy-form.liquid) so
    // that, until this component is actually running, a shopper cannot flip a radio that
    // nothing is there to wire up to the variant id that ships in the add-to-cart POST.
    // Re-enable them now, before sync() runs, so the two forms are interactive as soon as
    // they are also correctly synced -- never interactive-but-still-defaulted.
    this.querySelectorAll('fieldset[data-lp-formulas], fieldset[data-lp-sizes]').forEach((fieldset) => {
      fieldset.disabled = false;
    });

    // sync() must run before the eventBus subscription below, not after: if eventBus is
    // ever undefined, the assignment below throws and this component stops initializing
    // right there. Running sync() first means the shopper still gets a correctly synced
    // widget even in that failure case -- only the post-add-to-cart resync (the comment
    // below) would be missing, not the initial state.
    this.sync();

    // component-product-form.js's own success handler (processResponse ->
    // hideLoadingState -> toggleCartActionDisplay) unconditionally re-enables the submit
    // button once the add-to-cart request finishes, with no idea this widget may have
    // disabled it for an unrelated reason (a sold-out or unmatched combination the shopper
    // switched to *while that request was in flight*). Left alone, that race hands back an
    // enabled button labeled sold-out with an empty variant id. Re-running sync() after the
    // theme is done restores whatever state the currently selected combination actually
    // calls for. Order is safe: the theme emits cart:added only after hideLoadingState()
    // has already run, so this always executes last.
    this.onCartAdded = this.sync.bind(this);
    eventBus.on('cart:added', this.onCartAdded);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.root.removeEventListener('click', this.onThumbClick);
    this.root.removeEventListener('mouseover', this.onGalleryWarm);
    this.root.removeEventListener('focusin', this.onGalleryWarm);
    eventBus.off('cart:added', this.onCartAdded);
  }

  onChange(event) {
    if (!event.target.matches('[data-lp-formula], [data-lp-size]')) return;
    const isFormulaChange = event.target.matches('[data-lp-formula]');
    this.sync();
    // Size changes only swap the price already sitting next to both size cards (see
    // sync()'s own comment on [data-lp-size-price]) -- the brief calls out the formula
    // switch specifically (heading, gallery and price all move at once), not every size
    // pick, so only a formula change announces.
    if (isFormulaChange) this.announceFormula();
  }

  // Reads the shopper's own now-visible heading and total-price text off the DOM
  // (already updated by the sync() call above) instead of keeping a second copy of
  // either -- see this.liveRegion's own comment in connectedCallback for why this fires
  // here and not from sync() itself.
  announceFormula() {
    if (!this.liveRegion) return;

    const heading = this.root.querySelector('[data-lp-heading]:not([hidden])');
    if (!heading) return;

    const parts = [this.dataset.lpFormulaAnnounce, heading.textContent.trim()];
    const totalLabel = this.querySelector('[data-lp-total-label]');
    const nowPrice = this.querySelector('[data-lp-price]:not([hidden]) [data-lp-now]');
    if (totalLabel && nowPrice) {
      parts.push(`${totalLabel.textContent.trim()}: ${nowPrice.textContent.trim()}`);
    }
    const message = parts.filter(Boolean).join('. ');
    if (!message) return;

    // Clear-then-set on the next frame: some screen readers do not announce a live
    // region update when the new text is set in the same tick as the old one, or when
    // consecutive updates land before the previous announcement finished.
    this.liveRegion.textContent = '';
    requestAnimationFrame(() => {
      this.liveRegion.textContent = message;
    });
  }

  // Clicking (or Enter/Space-activating) a gallery thumbnail shows that gallery's
  // matching frame and hides the rest -- scoped to `closest('[data-lp-gallery]')` so a
  // formula-A thumb only ever touches formula A's own frames, never formula B's, even
  // though both galleries share this one root-level listener.
  //
  // Owner fix (gallery report, 12.08.2026): every frame is already rendered as its own
  // <img> inside [data-lp-gallery-main] (sections/lp-buy.liquid), main photo included,
  // in the same order as the thumbnails above it -- this used to instead overwrite a
  // single <img>'s `src` with the clicked thumb's data-lp-full URL and strip its
  // `srcset`/`sizes`. That broke two things at once: the main photo had no thumbnail
  // of its own to click back to (data-lp-full only ever existed on the SECOND photo
  // onward), and every swap after the first was a fresh network request for a URL the
  // browser had never fetched. Toggling `hidden` on already-rendered frames fixes
  // both: every frame has a thumbnail (including the first), and a click only flips
  // visibility of bytes already in flight or in cache -- never a src rewrite, never a
  // dropped srcset.
  //
  // Task: video frame -- `> img, > video` (direct children only), not a bare `img`
  // descendant selector: Shopify's video_tag filter renders a fallback <img> INSIDE
  // <video> (for browsers that cannot play it), so a plain `[data-lp-gallery-main] img`
  // would silently pick up that nested fallback as if it were its own frame -- same
  // frame count as expected (one extra img exactly cancels out the video that selector
  // never found), but the WRONG element, so clicking the video's own thumbnail hid
  // every real frame and never un-hid the actual <video>, leaving an empty stage. Caught
  // on a live DOM read after a click, not on the served HTML: the frame count on the
  // page as delivered was already correct either way.
  onThumbClick(event) {
    const thumb = event.target.closest('[data-lp-thumb]');
    if (!thumb) return;

    const gallery = thumb.closest('[data-lp-gallery]');
    const frames = gallery?.querySelectorAll('[data-lp-gallery-main] > img, [data-lp-gallery-main] > video');
    const frameIndex = Number(thumb.dataset.lpFrame);
    if (!frames || !frames[frameIndex]) return;

    // Same shared show/hide convention as every other switched pair in this widget --
    // the `hidden` IDL property, never `display` (see the static toggle() helper and
    // assets/lp-buy.css's file header).
    frames.forEach((frame, index) => {
      frame.hidden = index !== frameIndex;
    });

    gallery.querySelectorAll('[data-lp-thumb]').forEach((candidate) => {
      candidate.setAttribute('aria-pressed', String(candidate === thumb));
    });

    // Task: video frame -- clicking a photo thumbnail while the gallery's video frame
    // is mid-playback would otherwise leave it playing (audible, if unmuted by the
    // shopper) behind a now-hidden frame. Scoped to this one gallery, not the whole
    // widget: a click in formula A's strip must never touch formula B's video.
    LpBuy.pauseHiddenVideos(gallery);
  }

  // Owner fix (gallery report, 12.08.2026): a `loading="lazy"` frame that has never
  // scrolled near the viewport has also never started fetching, so clicking its
  // thumbnail would still cost a full network round-trip the first time -- the exact
  // complaint the src-swap above was replaced to fix, just moved one step later.
  // Hovering or focusing any thumbnail in a formula's strip is the shopper's earliest
  // signal that they are about to pick one, so that is the cue to switch every
  // remaining lazy frame in THAT formula's own stack over to eager: well after the
  // first screen (never lazy to begin with, see sections/lp-buy.liquid) but before the
  // click that actually needs the bytes. Reassigning `.loading` on a frame that is
  // already eager (or already loaded) is a harmless no-op, so this needs no "already
  // warmed" flag -- once every frame in a stack is eager, later calls simply find
  // nothing left with `loading="lazy"` to switch.
  onGalleryWarm(event) {
    const thumb = event.target.closest('[data-lp-thumb]');
    if (!thumb) return;

    const gallery = thumb.closest('[data-lp-gallery]');
    // Direct-child selector, same reasoning as onThumbClick's own frames query above --
    // a video frame's fallback <img> carries no `loading` attribute today, so this one
    // is not currently reachable by the bare descendant selector either way, but scoping
    // it to `>` keeps this query correct if that ever changes instead of relying on that
    // coincidence.
    const lazyFrames = gallery?.querySelectorAll('[data-lp-gallery-main] > img[loading="lazy"]');
    lazyFrames?.forEach((frame) => {
      frame.loading = 'eager';
    });
  }

  get formula() {
    return this.querySelector('[data-lp-formula]:checked')?.value;
  }

  get size() {
    return this.querySelector('[data-lp-size]:checked')?.value;
  }

  // Shared show/hide helper: every switched node (form, price, gallery, social proof,
  // heading, audience) uses the `hidden` IDL property, never `display` -- see the file
  // header in assets/lp-buy.css for why a `display` rule on any of these would be wrong.
  static toggle(elements, attr, match) {
    elements.forEach((el) => {
      el.hidden = el.getAttribute(attr) !== match;
    });
  }

  // Task: video frame -- a <video> that just went hidden (its own `hidden` attribute,
  // or an ancestor's, e.g. the whole formula's [data-lp-gallery] going hidden on a
  // formula switch) must stop playing, not keep running behind a `display: none` panel.
  // offsetParent is null exactly when an element or an ancestor is display:none -- true
  // for every `hidden` element here thanks to lp-core.css's own
  // ".lp [hidden] { display: none !important }" -- so this one check covers both a
  // whole gallery going hidden (sync(), below) and a single frame going hidden
  // (onThumbClick, above) with no separate code path for either.
  static pauseHiddenVideos(scope) {
    scope.querySelectorAll('video').forEach((video) => {
      if (!video.paused && video.offsetParent === null) {
        video.pause();
      }
    });
  }

  sync() {
    const formula = this.formula;
    const key = `${formula}-${this.size}`;

    // hidden is set as the IDL property (reflects to the attribute), not toggled via a
    // class: it takes the inactive form out of the tab order and the accessibility tree
    // for free, with no CSS dependency.
    LpBuy.toggle(this.querySelectorAll('[data-lp-form]'), 'data-lp-form', formula);
    LpBuy.toggle(this.querySelectorAll('[data-lp-price]'), 'data-lp-price', key);

    // Size-card prices are keyed the same "<formula>-<size>" way as [data-lp-price]
    // (snippets/lp-buy-form.liquid), but each node already lives inside the <label> for
    // ONE specific size -- only the formula half of the key is meaningful here. Matching
    // the full key (like [data-lp-price] above) would hide the price on every size
    // except whichever one happens to be selected; both size cards must show a price
    // regardless of which one is picked.
    this.querySelectorAll('[data-lp-size-price]').forEach((price) => {
      price.hidden = !price.getAttribute('data-lp-size-price').startsWith(`${formula}-`);
    });

    // Gallery, social proof, heading and "who it's for" live outside <lp-buy> -- see
    // this.root in connectedCallback.
    LpBuy.toggle(this.root.querySelectorAll('[data-lp-gallery]'), 'data-lp-gallery', formula);
    // Task: video frame -- runs right after the gallery toggle above, not before: a
    // formula switch mid-playback must stop that video, and this is the earliest point
    // in sync() where the just-hidden gallery's own `hidden` state is already in the DOM.
    LpBuy.pauseHiddenVideos(this.root);
    LpBuy.toggle(this.root.querySelectorAll('[data-lp-social-proof]'), 'data-lp-social-proof', formula);
    LpBuy.toggle(this.root.querySelectorAll('[data-lp-heading]'), 'data-lp-heading', formula);
    LpBuy.toggle(this.root.querySelectorAll('[data-lp-audience]'), 'data-lp-audience', formula);

    this.setVariant(key);
    this.setAccent(formula);
  }

  // --lp-brand/--lp-brand-dark (and, since review, the rest of the purple family --
  // --lp-brand-ink, --lp-lav, --lp-lav-100, --lp-lav-200) are set on this.root (the
  // section's own grid wrapper, not <lp-buy> itself) so the whole block -- including the
  // media column, outside <lp-buy> -- recolors through the same tokens every lp-buy.css
  // rule already reads. Colors themselves never live in this file: they arrive as
  // data-lp-accent-* attributes, computed by Liquid from the section's accent_a/accent_b
  // settings (see snippets/lp-buy-form.liquid).
  //
  // Review fix (minor 1): a formula with no accent configured (merchant cleared the
  // color picker in the editor) used to `return` here early, leaving whatever inline
  // value the PREVIOUS formula had just set -- switching A (accent set) -> B (accent
  // cleared) kept painting B in A's color instead of falling back to anything. Every
  // token below is now either set (accent present) or explicitly removed via
  // removeProperty (accent absent), so an absent accent correctly falls through to
  // lp-core.css's static .lp defaults instead of inheriting a stale JS-set value.
  setAccent(formula) {
    const prefix = formula === 'a' ? 'lpAccentA' : 'lpAccentB';
    const tokens = {
      '--lp-brand': this.dataset[prefix],
      '--lp-brand-dark': this.dataset[`${prefix}Dark`],
      '--lp-brand-ink': this.dataset[`${prefix}Ink`],
      '--lp-lav': this.dataset[`${prefix}Lav`],
      '--lp-lav-100': this.dataset[`${prefix}Lav100`],
      '--lp-lav-200': this.dataset[`${prefix}Lav200`],
    };
    Object.entries(tokens).forEach(([cssVar, value]) => {
      if (value) {
        this.root.style.setProperty(cssVar, value);
      } else {
        this.root.style.removeProperty(cssVar);
      }
    });
  }

  setVariant(key) {
    const active = this.querySelector(`[data-lp-form="${this.formula}"]`);
    if (!active) return;

    const input = active.querySelector('.product-variant-id');
    const button = active.querySelector('[name="add"]');
    if (!input || !button) return;

    // Combination has no [data-lp-variant] node (e.g. a size the other formula doesn't
    // carry): resolve `available` from that unconditionally and always write it below,
    // rather than returning early when the node is missing. Returning early would leave
    // input/button in Liquid's initially-rendered state -- enabled, holding whatever
    // variant id Liquid picked for that product -- so the shopper could add the wrong
    // variant with no visible sign anything is off. Disabling here, exactly like a
    // genuinely sold-out variant, is the outcome the mechanic is supposed to produce.
    const source = this.querySelector(`[data-lp-variant="${key}"]`);
    const available = !!source && source.dataset.lpAvailable === 'true';

    input.value = available ? source.dataset.lpId : '';
    input.disabled = !available;
    button.disabled = !available;

    const label = button.querySelector('[data-add-to-cart-text]');
    if (label) {
      label.textContent = available ? this.dataset.lpAddLabel : this.dataset.lpSoldLabel;
    }
  }
}

if (!customElements.get('lp-buy')) {
  customElements.define('lp-buy', LpBuy);
}

/* Consultation popup trigger (sections/lp-buy.liquid captures the trigger
   and the dialog as two SEPARATE strings, consult_trigger_html and
   consult_dialog_html, via snippets/lp-buy-consult.liquid -- part: 'trigger'
   / 'dialog'). snippets/lp-buy-form.liquid prints the trigger INSIDE
   fieldset[data-lp-formulas]'s own <legend> (owner decision 16.08.2026,
   shares its line via flex layout, assets/lp-buy.css) and the dialog
   separately, as a fieldset SIBLING of that <legend> -- <dialog> is not
   phrasing content, so it cannot live inside <legend> itself
   (docs/liquid-traps.md, trap 3). <lp-buy-consult> (defined below) wraps
   only the dialog now, not the trigger -- `this.querySelector` below
   therefore cannot find the trigger, it is not a descendant of this
   element anymore. `this.closest('lp-buy')` climbs back out to the shared
   ancestor both the trigger (inside its own fieldset's legend) and this
   dialog wrapper (inside the same fieldset, as a sibling) live under, then
   searches back down from there -- correctly scoped to ONE <lp-buy>
   instance even if a future page ever rendered this section twice.
   Reuses the exact <dialog>/showModal() mechanic already built for the
   license lightbox,
   assets/lp-reasons.js's LpReasonsLightbox -- Esc-to-close, the focus trap
   and returning focus to the trigger button on close are all native
   <dialog> behavior, not reimplemented here, same as there. Defined in this
   file rather than a second assets/lp-buy-consult.js: assets/lp-buy.js is
   already loaded unconditionally for this section (the <safe-load-scripts>
   tag lives inside <lp-buy> in snippets/lp-buy-form.liquid), unlike
   lp-reasons.js's own per-block, per-image conditional load, so a second
   file here would only add a second network request for no benefit. */
class LpBuyConsult extends HTMLElement {
  connectedCallback() {
    const scope = this.closest('lp-buy');
    this.trigger = scope ? scope.querySelector('[data-lp-buy-consult-open]') : null;
    this.dialog = this.querySelector('[data-lp-buy-consult-dialog]');
    this.closeButton = this.querySelector('[data-lp-buy-consult-close]');
    if (!this.trigger || !this.dialog) return;

    this.onTriggerClick = () => this.dialog.showModal();
    this.onCloseClick = () => this.dialog.close();
    // Same backdrop-only check as LpReasonsLightbox's own onDialogClick: a
    // click on the ::backdrop dispatches with the <dialog> itself as
    // event.target, a click on any real descendant sets target to that
    // descendant instead.
    this.onDialogClick = (event) => {
      if (event.target === this.dialog) this.dialog.close();
    };

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.dialog.addEventListener('click', this.onDialogClick);
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onCloseClick);
    }
  }

  disconnectedCallback() {
    if (!this.trigger || !this.dialog) return;
    this.trigger.removeEventListener('click', this.onTriggerClick);
    this.dialog.removeEventListener('click', this.onDialogClick);
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.onCloseClick);
    }
  }
}

if (!customElements.get('lp-buy-consult')) {
  customElements.define('lp-buy-consult', LpBuyConsult);
}
